import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'
import type { Frequency } from 'rrule'
import { RRule } from 'rrule'

import type { EventRecurrenceModel1 } from '../api/client'
import type { RecurrenceEventType } from '../types/ems'
import type { TimeUnits } from '../types/units'
import { attributeDurationToDayJsDuration } from './duration'
import { roundNumber } from './number'

export const formatTimezone = (timezone: string) => timezone.replace(/\//, ': ').replaceAll(/_/g, ' ')

export function convertTime(value: number, startUnit: TimeUnits, endUnit: TimeUnits): number {
  return dayjs
    .duration(value, attributeDurationToDayJsDuration[startUnit])
    .as(attributeDurationToDayJsDuration[endUnit])
}

export function secondsToMinutes(seconds: unknown) {
  if (typeof seconds !== 'number' || isNaN(seconds)) return null
  return roundNumber(seconds / 60)
}

export const rruleFrequencyMap = {
  [RRule.SECONDLY]: 'Secondly',
  [RRule.MINUTELY]: 'Minutely',
  [RRule.HOURLY]: 'Hourly',
  [RRule.DAILY]: 'Daily',
  [RRule.WEEKLY]: 'Weekly',
  [RRule.MONTHLY]: 'Monthly',
  [RRule.YEARLY]: 'Yearly'
}

export function formatRRuleFrequency(frequency: Frequency) {
  return rruleFrequencyMap[frequency] ?? ''
}

export type RecurrenceType = Required<EventRecurrenceModel1>['recurrenceType']
export function useRecurrenceFormatter() {
  const t = useTranslations('data.time')
  return useCallback((frequency?: RecurrenceType) => t(`recurrence_${frequency ?? 'SINGLE'}`), [t])
}

export function useFrequencyFormatter() {
  const t = useTranslations('data.time')
  return useCallback((frequency: RecurrenceEventType) => t(`frequencies_${frequency}`), [t])
}
